







































































































































  import { defineComponent, ref, computed, onMounted, onUnmounted, PropType } from 'vue'
  import uuid from 'uuid-random'
  import { Currency, Products, Stocks } from '@one/types'
  import { Cart, Wishlist } from '@one/types/dist/orderpath/app'
  import { ProductQuantityAndResourceId } from '../product/OneProductTileRightActions.vue'
  import { MappedProductStocks } from '~/store/stocks/types'
  import { ProductItem } from '~/plugins/gtm/gtm'
  import useUtils from '~/composables/useUtils'
  import useCurrentInstance from '~/utils/useCurrentInstance'
  import StockVisibilityMode = Stocks.StockVisibilityMode
  import useCoreUtils from '~/utils/useUtils'

  export default defineComponent({
    name: 'OneProductMinilistView',
    components: {
      OneProductTileGallery: () => import('~/components/organisms/product/OneProductTileGallery.vue'),
      OneProductTileInformation: () => import('~/components/organisms/product/OneProductTileInformation.vue'),
      OneProductTileRightActions: () => import('~/components/organisms/product/OneProductTileRightActions.vue'),
      OneProductStocksInfo: () => import('~/components/organisms/product/OneProductStocksInfo.vue'),
      OneProductTileRightPrice: () => import('~/components/organisms/product/OneProductTileRightPrice.vue'),
      OneProductTitleLabels: () => import('~/components/molecules/images/OneProductTitleLabels.vue'),
    },
    props: {
      extended: {
        type: Boolean,
        default: false,
      },
      showRightSide: {
        type: Boolean,
        default: true,
      },
      showAddToWishlistButton: {
        type: Boolean,
        default: true,
      },
      showTechTriggerBtn: {
        type: Boolean,
        default: true,
      },
      productId: {
        type: String,
        required: true,
      },
      unit: {
        type: String,
        required: true,
      },
      prices: {
        type: Object as PropType<Products.ProductPrice>,
      },
      priceLoading: {
        type: Boolean,
      },
      stocksLoading: {
        type: Boolean,
      },
      currency: {
        type: Object as PropType<Currency>,
      },
      progress: {
        type: Boolean,
        default: false,
      },
      progressWishlist: {
        type: Boolean,
        default: false,
      },
      carts: {
        type: Array as PropType<Cart.Responses.BasicCartInfo[]>,
        required: true,
      },
      wishlists: {
        type: Array as PropType<Wishlist.Responses.BasicWishlist[]>,
        required: true,
      },
      loginToBuyLabel: {
        type: String,
        required: true,
      },
    },
    emits: ['show-product', 'manufacturer-click', 'login-click', 'add-to-cart', 'add-to-wishlist'],
    setup(props, { emit }) {
      const vm = useCurrentInstance()

      const { formatProductPreorderDate } = useUtils()
      const { priceTypeLabel } = useCoreUtils(vm)

      const elemId = ref(uuid())
      const showCollapse = ref(false)
      const productQuantity = ref(0)
      const mainComponentClass = 'one-product-minilist-view'

      const tileRef = ref<any>(null)
      const inViewport = ref({ now: false })
      let observer: IntersectionObserver | null = null

      const getProduct = (id: string) => vm.$store.getters['products/getProduct'](id)
      const getWarehouse = (id: string) => vm.$store.getters['stocks/getWarehouse'](id)
      const getProductStocks = (id: string) => vm.$store.getters['stocks/getProductStocks'](id)
      const getProductWarehouse = (pId: string, wId: string) => vm.$store.getters['stocks/getProductWarehouse'](pId, wId)
      const getProductOrderQuantityInWarehouse = (pId: string, wId: string) => vm.$store.getters['stocks/getProductOrderQuantityInWarehouse'](pId, wId)
      const isEnoughProductWithBlockLabelInStock = (qty: number, labels: any, id: string) => vm.$store.getters['stocks/isEnoughProductWithBlockLabelInStock'](qty, labels, id)
      const getProductLabels = (label: string, id: string) => vm.$store.getters['products/getProductLabels'](label, id)
      const getProductImages = (id: string, size: string) => vm.$store.getters['products/getProductImages'](id, size)
      const getCategory = (cat: string) => vm.$store.getters['categories/getCategory'](cat)
      const productListImgSize = computed(() => vm.$store.getters['cms/productListImgSize'])

      const theme = computed(() => vm.$store.getters['cms/theme'])
      const getDefaultWarehouseId = computed(() => vm.$store.getters['stocks/getDefaultWarehouseId'])
      const getProductPrice = computed(() => vm.$store.getters['products/getProductPrice'] || (() => null))

      onMounted(() => {
        if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
          observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
              inViewport.value.now = true
              vm.$plugins.onMounted(mainComponentClass, vm, { productId: props.productId })

              const el = tileRef.value?.$el || tileRef.value
              if (observer && el) observer.unobserve(el)
            }
          }, { rootMargin: '400px 0px' })

          const el = tileRef.value?.$el || tileRef.value
          if (el) observer.observe(el)
        } else {
          inViewport.value.now = true
          vm.$plugins.onMounted(mainComponentClass, vm, { productId: props.productId })
        }
      })

      onUnmounted(() => {
        vm.$plugins.onUnmounted(mainComponentClass)
        if (observer) observer.disconnect()
      })

      const product = computed(() => getProduct(props.productId))
      const isProductBlocked = computed(() => getProductLabels('Block', props.productId))
      const preorderDate = computed(() => formatProductPreorderDate(product.value?.preorderDate))
      const categoryName = computed(() => getCategory(product.value?.canonicalCategory)?.name || null)

      const notEnoughProductStockAmount = computed(() => {
        return !!(
          product.value &&
          isEnoughProductWithBlockLabelInStock(productQuantity.value, product.value.labels, product.value.id)
        )
      })

      const stocks = computed<MappedProductStocks | null>(() => getProductStocks(props.productId))
      const stockVisibilityModeHidden = computed(() => stocks.value?.stockVisibilityMode === StockVisibilityMode.HIDDEN)

      const currentWarehouse = computed(() => getWarehouse(getDefaultWarehouseId.value))
      const parentWarehouse = computed(() => currentWarehouse.value ? getWarehouse(currentWarehouse.value.parent) : null)
      const parentWarehouseId = computed(() => parentWarehouse.value?.id || '')
      const hasSelectedWarehouseParent = computed(() => parentWarehouseId.value !== '')
      const isSelectedWarehouseCentral = computed(() => currentWarehouse.value?.type?.toString() === 'CENTRAL')

      const warehouse = (id: string) => getProductWarehouse(props.productId, id)
      const getLabel = (id: string) => warehouse(id) ? warehouse(id).warehouseLabel : '-'
      const getShippingTime = (id: string) => getWarehouse(id)?.shippingTime
      const getAvailability = (id: string) => warehouse(id)?.available

      const labels = computed(() => ({
        firstWarehouse: getLabel(parentWarehouseId.value),
        defaultWarehouse: getLabel(getDefaultWarehouseId.value),
      }))

      const shippingTime = computed(() => ({
        firstWarehouse: getShippingTime(parentWarehouseId.value),
        defaultWarehouse: getShippingTime(getDefaultWarehouseId.value),
      }))

      const mediumPhotoExtended = (id: string) => {
        return getProductImages(id, props.extended ? 'SMALL_300' : productListImgSize.value)
      }

      const warehouses = computed(() => {
        if (!stocks.value) return []
        return stocks.value.warehouses.allIds
          .map((stockId: string) => ({
            id: stockId,
            unity: stocks.value!.unit,
            quantity: getProductOrderQuantityInWarehouse(props.productId, stockId),
            label: getLabel(stockId),
            shippingTime: getShippingTime(stockId),
            type: getWarehouse(stockId)?.type,
            available: getAvailability(stockId),
            stockVisibilityModeHidden: stockVisibilityModeHidden.value,
          }))
          .filter((item: any) => {
            if (parentWarehouseId.value) {
              return item.id !== getDefaultWarehouseId.value && item.id !== parentWarehouseId.value
            }
            return item.id !== getDefaultWarehouseId.value
          })
          .sort((a: any, b: any) => {
            if (a.label < b.label) return -1
            if (a.label > b.label) return 1
            return 0
          })
      })

      const onAddToCart = ({ id: cartId, quantity: amountOfProducts }: ProductQuantityAndResourceId) => {
        emit('add-to-cart', {
          cartId,
          productId: product.value.id,
          amountOfProducts,
        })
      }

      const onAddToWishlist = ({ id: wishlistId, quantity: amountOfProducts }: ProductQuantityAndResourceId) => {
        emit('add-to-wishlist', {
          wishlistId,
          productId: product.value.id,
          amountOfProducts,
        })
      }

      const onProductQuantityChanged = (quantity: number) => {
        productQuantity.value = quantity
      }

      const onProductClick = (prod: any) => {
        const price = getProductPrice.value(prod.id)
        vm.$gtm.selectItem(
          {
            item_id: prod.id,
            item_name: prod.name,
            item_brand: prod.brand?.name,
            category: vm.$routes.getCategoryPath(prod.canonicalCategory) || '',
            price: price?.price || 0,
            discount: price ? price.catalogPrice - price.price : 0,
          } as ProductItem,
          'one-product-minilist-view',
        )
      }

      return {
        tileRef,
        inViewport,
        priceTypeLabel,

        elemId,
        showCollapse,
        mainComponentClass,

        theme,
        product,
        categoryName,
        preorderDate,
        isProductBlocked,
        notEnoughProductStockAmount,
        stocks,
        warehouses,
        parentWarehouseId,
        hasSelectedWarehouseParent,
        isSelectedWarehouseCentral,
        labels,
        shippingTime,

        getProductImages,
        mediumPhotoExtended,

        onAddToCart,
        onAddToWishlist,
        onProductQuantityChanged,
        onProductClick,
      }
    },
  })
